import { createContext, useContext, useEffect, ReactNode } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin, AppInsightsContext as AIContext } from '@microsoft/applicationinsights-react-js';
import {
  initializeAppInsights,
  setAuthenticatedUser,
  clearAuthenticatedUser
} from '../services/appInsights';
import { useAuth } from './AuthContext';

interface AppInsightsContextType {
  appInsights: ApplicationInsights | null;
  reactPlugin: ReactPlugin | null;
}

const AppInsightsContext = createContext<AppInsightsContextType>({
  appInsights: null,
  reactPlugin: null
});

export function AppInsightsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { appInsights, reactPlugin } = initializeAppInsights();

  useEffect(() => {
    if (user) {
      setAuthenticatedUser(user.id, user.email);
    } else {
      clearAuthenticatedUser();
    }
  }, [user]);

  const contextValue: AppInsightsContextType = {
    appInsights,
    reactPlugin
  };

  if (reactPlugin) {
    return (
      <AppInsightsContext.Provider value={contextValue}>
        <AIContext.Provider value={reactPlugin}>{children}</AIContext.Provider>
      </AppInsightsContext.Provider>
    );
  }

  return <AppInsightsContext.Provider value={contextValue}>{children}</AppInsightsContext.Provider>;
}

export function useAppInsights(): AppInsightsContextType {
  return useContext(AppInsightsContext);
}
